import React, { useCallback, useEffect, useState } from "react";
import { Card, CardBody, CardHeader, CardTitle, Spinner } from "reactstrap";
import Button from "react-bootstrap/Button";

import { apiFetch, API, getMarket, getUserLogin } from "../../services/api";
import LeverEditor from "../../components/experiments/LeverEditor";
import {
    EXPERIMENT_SURFACES,
    apiErrorMessage,
    configToLevers,
    describeConfig,
    leversToConfig,
    parseSurfaceConfigResponse,
    surfaceLabel,
} from "../../helpers/experiments";

const fmtDateTime = (d) => {
    if (!d) return null;
    const date = new Date(d);
    if (isNaN(date.getTime()) || date.getFullYear() < 1971) return null;
    return date.toLocaleString("fr-FR");
};

// Défauts de surface : la config appliquée à TOUT LE MONDE, hors expérience.
//
// C'est le pendant permanent du registre A/B : conclure une expérience sur une
// variante « en données » recopie sa config ici. La page sert à relire ce que le
// site applique réellement (JSON brut + résumé en français), à le corriger à la
// main avec le MÊME éditeur de leviers que la modale d'expérience, et à revenir
// aux défauts d'origine (config vide).
//
// `PUT /surface-config` REMPLACE la config de la surface (pas de fusion) : la
// page envoie donc toujours la config complète qu'elle vient d'éditer.
export function SurfaceConfig() {
    const [configs, setConfigs] = useState(null);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null); // surface en cours d'édition
    const [levers, setLevers] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [notice, setNotice] = useState(null);

    const load = useCallback(() => {
        setError(null);
        // Config non internationalisée : une seule config par surface.
        apiFetch(API.back + "/surface-config", { method: "GET", market: false })
            .then((res) => {
                if (!res.ok) throw new Error("HTTP " + res.status);
                return res.json();
            })
            .then((data) => setConfigs(parseSurfaceConfigResponse(data)))
            .catch((err) => {
                console.log(err);
                setError("Défauts de surface indisponibles (" + err.message + ") — vérifier la connexion au back-office.");
                setConfigs(parseSurfaceConfigResponse(null));
            });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const startEditing = (surface) => {
        setSaveError(null);
        setNotice(null);
        setEditing(surface);
        setLevers(configToLevers(surface, configs[surface].config));
    };

    const put = (surface, config, successMessage) => {
        setSaving(true);
        setSaveError(null);
        setNotice(null);
        apiFetch(API.back + "/surface-config", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ surface, config, updatedBy: getUserLogin() }),
        })
            .then((res) => {
                // L'API refuse les clés inconnues, une couleur de CTA invalide,
                // un libellé de plus de 60 caractères ou une config de plus de
                // 8 Ko : son message dit quoi corriger, on l'affiche tel quel.
                if (!res.ok) {
                    return res.text().then((body) => Promise.reject(new Error(apiErrorMessage(res.status, body))));
                }
                setSaving(false);
                setEditing(null);
                setNotice(successMessage);
                load();
            })
            .catch((err) => {
                console.log(err);
                setSaving(false);
                setSaveError("Enregistrement impossible : " + err.message);
            });
    };

    const save = (surface) => put(surface, leversToConfig(surface, levers), "Défauts de la surface enregistrés.");

    const reset = (surface) => {
        const label = surfaceLabel(surface);
        const confirmed = window.confirm(
            "Réinitialiser les défauts de « " +
                label +
                " » ?\n\nTous les leviers de cette surface seront effacés et le site reviendra à son rendu d'origine, " +
                "immédiatement et pour tout le monde."
        );
        if (!confirmed) return;
        put(surface, {}, "Défauts de « " + label + " » réinitialisés.");
    };

    return (
        <Card className="flex-fill w-100">
            <CardHeader>
                <CardTitle tag="h5" className="mb-0">
                    Défauts de surface
                </CardTitle>
                <div className="text-muted small mt-2">
                    Config appliquée à tout le monde, hors expérience. Conclure une expérience sur une variante « en
                    données » recopie sa config ici. Une surface sans levier rend le site tel qu'il est codé.
                </div>
            </CardHeader>
            <CardBody>
                {error ? <div className="alert alert-danger">{error}</div> : null}
                {notice ? <div className="alert alert-success">{notice}</div> : null}
                {!configs && !error ? (
                    <div className="d-flex align-items-center">
                        <Spinner color="primary" size="sm" />
                        <span className="text-muted ml-2">Chargement…</span>
                    </div>
                ) : null}

                {configs
                    ? EXPERIMENT_SURFACES.map(({ value: surface, label }) => {
                          const entry = configs[surface] || { config: {}, updatedAt: "", updatedBy: "" };
                          const summary = describeConfig(surface, entry.config);
                          const isEmpty = Object.keys(entry.config || {}).length === 0;
                          const updatedAt = fmtDateTime(entry.updatedAt);

                          return (
                              <Card key={surface} className="mb-4">
                                  <CardHeader>
                                      <div className="d-flex justify-content-between align-items-center">
                                          <CardTitle tag="h5" className="mb-0">
                                              {label}
                                          </CardTitle>
                                          <div>
                                              <Button
                                                  variant="outline-secondary"
                                                  size="sm"
                                                  className="mr-2"
                                                  disabled={saving || editing === surface}
                                                  onClick={() => startEditing(surface)}
                                              >
                                                  Éditer
                                              </Button>
                                              <Button
                                                  variant="outline-danger"
                                                  size="sm"
                                                  disabled={saving || isEmpty}
                                                  onClick={() => reset(surface)}
                                              >
                                                  Réinitialiser
                                              </Button>
                                          </div>
                                      </div>
                                      {updatedAt || entry.updatedBy ? (
                                          <div className="text-muted small mt-1">
                                              Dernière modification {updatedAt ? "le " + updatedAt : ""}
                                              {entry.updatedBy ? " par " + entry.updatedBy : ""}.
                                          </div>
                                      ) : null}
                                  </CardHeader>
                                  <CardBody>
                                      {isEmpty ? (
                                          <p className="text-muted">
                                              Aucun levier : la surface rend ses valeurs par défaut (le site tel qu'il est
                                              codé).
                                          </p>
                                      ) : (
                                          <React.Fragment>
                                              {summary.length > 0 ? (
                                                  <ul className="pl-3">
                                                      {summary.map((line, index) => (
                                                          <li key={index}>{line}</li>
                                                      ))}
                                                  </ul>
                                              ) : (
                                                  <p className="text-muted">
                                                      Config non vide mais sans levier connu : voir le JSON ci-dessous.
                                                  </p>
                                              )}
                                              <pre className="bg-light p-2 small mb-0" style={{ overflowX: "auto" }}>
                                                  {JSON.stringify(entry.config, null, 2)}
                                              </pre>
                                          </React.Fragment>
                                      )}

                                      {editing === surface ? (
                                          <div className="mt-4">
                                              <hr />
                                              <h5>Éditer les défauts</h5>
                                              {saveError ? <div className="alert alert-danger">{saveError}</div> : null}
                                              <LeverEditor
                                                  surface={surface}
                                                  levers={levers}
                                                  market={getMarket()}
                                                  showVariantKey={false}
                                                  onChange={(changes) =>
                                                      setLevers((current) => ({ ...current, ...changes }))
                                                  }
                                              />
                                              <div className="text-muted small mb-3">
                                                  L'enregistrement REMPLACE la config de la surface : un levier vidé ici
                                                  disparaît des défauts.
                                              </div>
                                              <Button
                                                  variant="primary"
                                                  size="sm"
                                                  className="mr-2"
                                                  disabled={saving}
                                                  onClick={() => save(surface)}
                                              >
                                                  {saving ? "Enregistrement…" : "Enregistrer"}
                                              </Button>
                                              <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  disabled={saving}
                                                  onClick={() => {
                                                      setEditing(null);
                                                      setSaveError(null);
                                                  }}
                                              >
                                                  Annuler
                                              </Button>
                                          </div>
                                      ) : null}
                                  </CardBody>
                              </Card>
                          );
                      })
                    : null}
            </CardBody>
        </Card>
    );
}

export default SurfaceConfig;
